import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Wallet, Search, ChevronDown, X, Building2, CreditCard, TrendingUp, History, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ModuleWalletManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch wallets and tenants in parallel
      const [walletsRes, tenantsRes] = await Promise.all([
        axios.get(`${API}/api/admin/modules/wallets`, { headers }),
        axios.get(`${API}/api/admin/tenants`, { headers }).catch(() => ({ data: [] }))
      ]);

      setWallets(walletsRes.data.wallets || []);
      // API returns array directly, not { tenants: [...] }
      setTenants(Array.isArray(tenantsRes.data) ? tenantsRes.data : (tenantsRes.data.tenants || []));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const getTenantName = (tenantId) => {
    if (tenantId === 'default') return 'Default Tenant';
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant?.name || tenant?.domain || `Tenant ${tenantId?.substring(0, 8)}...`;
  };

  const fetchTransactions = async (tenantId) => {
    setLoadingTransactions(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/wallet/transactions/${tenantId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleWalletClick = (wallet) => {
    setSelectedWallet(wallet);
    setShowDetailsModal(true);
    fetchTransactions(wallet.tenant_id);
  };

  const filteredWallets = wallets.filter(wallet => {
    const tenantName = getTenantName(wallet.tenant_id).toLowerCase();
    const tenantId = wallet.tenant_id?.toLowerCase() || '';
    const query = searchQuery.toLowerCase();
    
    // Filter by search query
    const matchesSearch = tenantName.includes(query) || tenantId.includes(query);
    
    // Filter by selected tenant
    const matchesTenant = !selectedTenantId || wallet.tenant_id === selectedTenantId;
    
    return matchesSearch && matchesTenant;
  });

  const formatCurrency = (value) => {
    return `₹${parseFloat(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  const formatCredits = (value) => {
    return `${parseFloat(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Credits`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading wallet data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin Panel
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Module Wallet Management</h1>
              <p className="text-gray-600 mt-2">View and manage tenant module credit balances</p>
            </div>
            <Button onClick={fetchData} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Tenant Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Tenant</label>
              <select
                value={selectedTenantId}
                onChange={(e) => setSelectedTenantId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Tenants ({wallets.length})</option>
                {wallets.map(wallet => (
                  <option key={wallet.tenant_id} value={wallet.tenant_id}>
                    {getTenantName(wallet.tenant_id)}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Search by tenant name or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Tenants</p>
                <p className="text-2xl font-bold text-gray-900">{wallets.length}</p>
              </div>
              <Building2 className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Balance</p>
                <p className="text-2xl font-bold text-green-600" data-testid="module-wallet-total-balance-credits">
                  {formatCredits(wallets.reduce((sum, w) => sum + (w.balance || 0), 0))}
                </p>
              </div>
              <Wallet className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Operations</p>
                <p className="text-2xl font-bold text-purple-600">
                  {wallets.reduce((sum, w) => sum + (w.metadata?.total_operations || 0), 0)}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Lifetime Spent</p>
                <p className="text-2xl font-bold text-amber-600">
                  {formatCurrency(wallets.reduce((sum, w) => sum + (w.metadata?.lifetime_spent || 0), 0))}
                </p>
              </div>
              <CreditCard className="w-8 h-8 text-amber-500" />
            </div>
          </div>
        </div>

        {/* Wallets Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWallets.map((wallet) => {
            const moduleCredits = wallet.module_credits || {};
            const totalCredits = (moduleCredits.retail || 0) + (moduleCredits.apf || 0) + (moduleCredits.ec_extractor || 0);
            
            return (
              <Card 
                key={wallet.tenant_id} 
                className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-blue-300"
                onClick={() => handleWalletClick(wallet)}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    {getTenantName(wallet.tenant_id)}
                  </CardTitle>
                  <CardDescription className="flex items-center justify-between">
                    <span>Balance: {formatCredits(wallet.balance)}</span>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">Click for details</span>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center bg-blue-50 px-3 py-2 rounded">
                      <span className="text-sm font-medium text-gray-700">Retail Credits</span>
                      <span className="text-lg font-bold text-blue-600">{moduleCredits.retail || 0}</span>
                    </div>
                    <div className="flex justify-between items-center bg-purple-50 px-3 py-2 rounded">
                      <span className="text-sm font-medium text-gray-700">APF Credits</span>
                      <span className="text-lg font-bold text-purple-600">{moduleCredits.apf || 0}</span>
                    </div>
                    <div className="flex justify-between items-center bg-green-50 px-3 py-2 rounded">
                      <span className="text-sm font-medium text-gray-700">EC Extractor</span>
                      <span className="text-lg font-bold text-green-600">{moduleCredits.ec_extractor || 0}</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-3 border-t text-xs text-gray-600 grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-gray-500">Operations:</span>
                      <span className="ml-1 font-medium">{wallet.metadata?.total_operations || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Spent:</span>
                      <span className="ml-1 font-medium">{formatCurrency(wallet.metadata?.lifetime_spent || 0)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredWallets.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <Wallet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No wallets found matching your criteria</p>
          </div>
        )}
      </div>

      {/* Wallet Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              {selectedWallet && getTenantName(selectedWallet.tenant_id)}
            </DialogTitle>
          </DialogHeader>
          
          {selectedWallet && (
            <div className="space-y-6">
              {/* Wallet Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Tenant ID</p>
                  <p className="font-mono text-sm break-all">{selectedWallet.tenant_id}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="font-medium capitalize">{selectedWallet.status}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Current Balance</p>
                  <p className="text-2xl font-bold text-green-600">{formatCredits(selectedWallet.balance)}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Lifetime Spent</p>
                  <p className="text-2xl font-bold text-amber-600">{formatCurrency(selectedWallet.metadata?.lifetime_spent || 0)}</p>
                </div>
              </div>

              {/* Module Credits */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Module Credits</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-600">Retail</p>
                    <p className="text-xl font-bold text-blue-600">{selectedWallet.module_credits?.retail || 0}</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-600">APF</p>
                    <p className="text-xl font-bold text-purple-600">{selectedWallet.module_credits?.apf || 0}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-600">EC Extractor</p>
                    <p className="text-xl font-bold text-green-600">{selectedWallet.module_credits?.ec_extractor || 0}</p>
                  </div>
                </div>
              </div>

              {/* Metadata */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Statistics</h3>
                <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Total Operations:</span>
                    <span className="ml-2 font-medium">{selectedWallet.metadata?.total_operations || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Total Purchased:</span>
                    <span className="ml-2 font-medium">{formatCredits(selectedWallet.metadata?.total_purchased || 0)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Created:</span>
                    <span className="ml-2 font-medium">{formatDate(selectedWallet.created_at)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Last Updated:</span>
                    <span className="ml-2 font-medium">{formatDate(selectedWallet.updated_at)}</span>
                  </div>
                </div>
              </div>

              {/* Recent Transactions */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Recent Transactions
                </h3>
                {loadingTransactions ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : transactions.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {transactions.slice(0, 10).map((txn, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded text-sm">
                        <div>
                          <span className="font-medium">{txn.type || txn.txn_type}</span>
                          <span className="text-gray-500 ml-2">{formatDate(txn.created_at)}</span>
                        </div>
                        <span className={`font-medium ${txn.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {txn.amount > 0 ? '+' : ''}{formatCredits(txn.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No transactions found</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModuleWalletManagement;
